import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/base/EmptyState";
import SectionHeading from "@/components/base/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  formatCount,
  formatDate,
  getCategoryName,
  getRelatedVideos,
  getVideoById,
} from "@/utils/video";

export default function DetailPage() {
  const { id = "" } = useParams();
  const video = getVideoById(id);
  const categoryName = video ? getCategoryName(video.category) : "视频";
  const categoryPath = video ? `/category/${video.category}` : "/category/all";

  usePageMeta(
    video ? `${video.title} - 色色视频` : "视频不存在 - 色色视频",
    video
      ? video.summary.slice(0, 118)
      : "该视频可能已下架或链接有误，可返回首页继续浏览其他视频内容。",
    video ? `${video.title},${categoryName},色色视频,${video.tags.join(",")}` : "视频不存在,色色视频",
  );

  if (!video) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="container-page flex-1 py-12 md:py-16">
          <EmptyState
            icon="ri-file-unknow-line"
            title="视频不存在"
            description="你访问的视频可能已下架、调整分类，或链接输入有误。可以返回首页或浏览全部分类内容。"
          >
            <Link
              to="/"
              className="focus-ring inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-5-line text-sm" aria-hidden="true" />
              返回首页
            </Link>
            <Link
              to="/category/all"
              className="focus-ring inline-flex items-center gap-1.5 rounded-md border border-background-300/80 bg-white px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-secondary-300"
            >
              浏览全部视频
            </Link>
          </EmptyState>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedVideos(video, 4);

  const infoRows = [
    { label: "类别", value: categoryName },
    { label: "时长", value: video.duration },
    { label: "发布年份", value: `${video.year}` },
    { label: "地区", value: video.region },
    { label: "导演 / 制片方", value: video.director },
    { label: "收录时间", value: formatDate(video.publishedAt) },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="container-page pt-4 md:pt-5">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: categoryName, to: categoryPath },
              { label: video.title },
            ]}
          />
        </div>

        <section className="container-page pt-5">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-primary-50 px-2 py-1 text-[11px] font-medium text-primary-700">
                  {categoryName}
                </span>
                <span className="inline-flex items-center gap-1 rounded bg-accent-50 px-2 py-1 font-label text-[11px] font-medium text-accent-800">
                  <i className="ri-star-fill text-[10px]" aria-hidden="true" />
                  {video.rating.toFixed(1)}
                </span>
                <span className="text-[11px] text-foreground-500">
                  收录于 {formatDate(video.publishedAt)}
                </span>
              </div>

              <h1 className="mt-3 text-2xl font-semibold leading-tight text-foreground-950 md:text-[34px]">
                {video.title}
              </h1>

              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 font-label text-[11px] text-foreground-500 md:text-xs">
                <span>{video.year}</span>
                <span className="text-background-300">/</span>
                <span>{video.region}</span>
                <span className="text-background-300">/</span>
                <span>{video.duration}</span>
                <span className="text-background-300">/</span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-eye-line text-xs" aria-hidden="true" />
                  {formatCount(video.views)} 次浏览
                </span>
                <span className="text-background-300">/</span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-bookmark-line text-xs" aria-hidden="true" />
                  {formatCount(video.favorites)} 收藏
                </span>
              </div>

              <div className="relative mt-5 aspect-video w-full overflow-hidden rounded-lg border border-background-200/90 bg-primary-900">
                <img
                  src={video.cover}
                  alt={`${video.title} 视频封面大图`}
                  title={`${video.title} - ${categoryName}视频封面`}
                  className="h-full w-full object-cover object-top"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 flex flex-wrap items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded bg-primary-950/70 px-2 py-1 text-[10px] text-background-100 md:text-[11px]">
                    <i className="ri-information-line text-xs" aria-hidden="true" />
                    内容信息展示 · 本站不提供在线播放
                  </span>
                  <span className="rounded bg-primary-950/70 px-2 py-1 font-label text-[10px] text-background-100 md:text-[11px]">
                    {video.duration}
                  </span>
                </div>
              </div>

              <div className="mt-6 md:mt-8">
                <h2 className="flex items-center gap-2 text-base font-semibold text-foreground-950 md:text-lg">
                  <span className="h-4 w-1 rounded-full bg-accent-500" aria-hidden="true" />
                  内容简介
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-600">{video.summary}</p>
                <p className="mt-2 text-sm leading-7 text-foreground-500">
                  本页仅汇总该视频的类别、时长、发布年份与制作信息，供浏览与筛选参考。若对内容有补充或纠错建议，可通过关于页的反馈邮箱联系我们。
                </p>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-foreground-400">标签：</span>
                {video.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="focus-ring inline-flex items-center gap-1 rounded-full border border-background-300/80 bg-white px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-secondary-300 hover:text-secondary-700"
                  >
                    <i className="ri-hashtag text-[10px]" aria-hidden="true" />
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-background-200/80 pt-5">
                <Link
                  to={categoryPath}
                  className="focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-arrow-left-line text-sm" aria-hidden="true" />
                  返回{categoryName}分类
                </Link>
                <Link
                  to="/category/all"
                  className="focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/80 bg-white px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-secondary-300"
                >
                  浏览全部视频
                </Link>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-[88px]">
                <div className="surface-card overflow-hidden">
                  <div className="border-b border-background-200/80 px-4 py-3">
                    <h2 className="font-heading text-sm font-semibold text-foreground-950">
                      基本信息
                    </h2>
                    <p className="mt-0.5 font-label text-[10px] uppercase tracking-[0.16em] text-foreground-400">
                      Video Info
                    </p>
                  </div>
                  <dl className="divide-y divide-background-100">
                    {infoRows.map((row) => (
                      <div key={row.label} className="flex items-start justify-between gap-4 px-4 py-2.5">
                        <dt className="shrink-0 text-[11px] text-foreground-400">{row.label}</dt>
                        <dd className="text-right text-[11px] font-medium text-foreground-800 md:text-xs">
                          {row.value}
                        </dd>
                      </div>
                    ))}
                    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
                      <dt className="shrink-0 text-[11px] text-foreground-400">站内热度</dt>
                      <dd className="flex items-center gap-1 text-right text-[11px] font-medium text-accent-700 md:text-xs">
                        <i className="ri-fire-line text-xs" aria-hidden="true" />
                        {formatCount(video.views)}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="mt-4 rounded-lg border border-secondary-100 bg-secondary-50 p-4">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold text-secondary-800">
                    <i className="ri-shield-check-line text-sm" aria-hidden="true" />
                    浏览说明
                  </p>
                  <p className="mt-2 text-[11px] leading-relaxed text-foreground-600">
                    色色视频为内容聚合展示平台，仅提供视频信息索引与分类浏览，不提供播放、下载、会员或付费入口。
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="container-page pt-10 md:pt-12" aria-labelledby="related-heading">
          <div id="related-heading">
            <SectionHeading
              eyebrow="You May Also Like"
              title="相关推荐"
              description="根据当前视频的分类、标签与地区进行匹配，帮助你继续发现相近的内容。"
              actionLabel={`更多${categoryName}`}
              actionTo={categoryPath}
            />
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {related.map((item) => (
              <VideoCard key={item.id} video={item} />
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}